import OrderCargoOrderDetailsComplexResponse, {
  OrderCargoOrderDetailsComplexResponseDataDispatcher,
  OrderCargoOrderDetailsComplexResponseDataNodeExclusions,
  OrderCargoOrderDetailsComplexResponseDataCandidatureDriver,
  OrderCargoOrderDetailsComplexResponseDataCandidature,
  OrderCargoOrderDetailsComplexResponseDataTransportingOrderDriver,
  OrderCargoOrderDetailsComplexResponseDataDriverSolveNodeCheckoutEvent,
  OrderCargoOrderDetailsComplexResponseDataDriverSolveNode,
  OrderCargoOrderDetailsComplexResponseDataPassengerInternal,
  OrderCargoOrderDetailsComplexResponseDataPassengerExternal,
  OrderCargoOrderDetailsComplexResponseDataPassenger,
  OrderCargoOrderDetailsComplexResponseDataPassengerType,
  OrderCargoOrderDetailsComplexResponseDataSolveNode,
  OrderCargoOrderDetailsComplexResponseDataSolveStage,
  OrderCargoOrderDetailsComplexResponseDataTransportingOrder,
  OrderCargoOrderDetailsComplexResponseData,
} from "../../../../api/raily/order/cargo-order/details-complex/order-cargo-order-details-complex.response";
import SourceType from "../../../../types/source-type";
import OrderCargoOrderDetailsComplex, {
  OrderCargoOrderDetailsComplexCandidature,
  OrderCargoOrderDetailsComplexCandidatureDriver,
  OrderCargoOrderDetailsComplexDispatcher,
  OrderCargoOrderDetailsComplexDriverSolveNode,
  OrderCargoOrderDetailsComplexDriverSolveNodeCheckoutEvent,
  OrderCargoOrderDetailsComplexNodeExclusions,
  OrderCargoOrderDetailsComplexPassenger,
  OrderCargoOrderDetailsComplexPassengerExternal,
  OrderCargoOrderDetailsComplexPassengerInternal,
  OrderCargoOrderDetailsComplexSolveNode,
  OrderCargoOrderDetailsComplexSolveStage,
  OrderCargoOrderDetailsComplexTransportingOrder,
  OrderCargoOrderDetailsComplexTransportingOrderDriver,
} from "./order-cargo-order-details-complex";

const createDispatcher = (
  dispatcher: OrderCargoOrderDetailsComplexResponseDataDispatcher,
): OrderCargoOrderDetailsComplexDispatcher => {
  return {
    uuid: dispatcher.id,
  };
};

const createNodeExclusions = (
  nodeExclusions: OrderCargoOrderDetailsComplexResponseDataNodeExclusions,
): OrderCargoOrderDetailsComplexNodeExclusions => {
  return {
    excludeApproaching: nodeExclusions.excludeApproaching,
    excludeInside: nodeExclusions.excludeInside,
    excludeReturning: nodeExclusions.excludeReturning,
    nodeIds: nodeExclusions.nodeIds,
  };
};

const createCandidatureDriver = (
  driver: OrderCargoOrderDetailsComplexResponseDataCandidatureDriver,
): OrderCargoOrderDetailsComplexCandidatureDriver => {
  return {
    firstName: driver.firstName,
    lastName: driver.lastName,
    mobilePrimary: driver.mobilePrimary,
    mobilePrimaryPrefix: driver.mobilePrimaryPrefix,
    mobileSecondary: driver.mobileSecondary,
    mobileSecondaryPrefix: driver.mobileSecondaryPrefix,
    uuid: driver.id,
  };
};

const createCandidatureEntry = (
  entry: OrderCargoOrderDetailsComplexResponseDataCandidature,
): OrderCargoOrderDetailsComplexCandidature => {
  return {
    candidatureUuid: entry.candidatureId,
    createdAt: entry.createdAt,
    createdBy: entry.createdBy,
    driver: createCandidatureDriver(entry.driver),
    modifiedAt: entry.modifiedAt,
    modifiedBy: entry.modifiedBy,
    uuid: entry.id,
  };
};

const createCandidatureEntries = (
  entries: OrderCargoOrderDetailsComplexResponseDataCandidature[],
): OrderCargoOrderDetailsComplexCandidature[] => {
  return entries.map(createCandidatureEntry);
};

const createDriver = (
  driver: OrderCargoOrderDetailsComplexResponseDataTransportingOrderDriver | null,
): OrderCargoOrderDetailsComplexTransportingOrderDriver | null => {
  if (!driver) return null;

  return {
    firstName: driver.firstName,
    lastName: driver.lastName,
    mobilePrimary: driver.mobilePrimary,
    mobilePrimaryPrefix: driver.mobilePrimaryPrefix,
    uuid: driver.id,
  };
};

const createDriverSolveNodeCheckoutEvent = (
  checkoutEvent: OrderCargoOrderDetailsComplexResponseDataDriverSolveNodeCheckoutEvent | null,
): OrderCargoOrderDetailsComplexDriverSolveNodeCheckoutEvent | null => {
  if (!checkoutEvent) return null;
  return {
    achievedAt: checkoutEvent.achievedAt,
  };
};

const createDriverSolveNode = (
  node: OrderCargoOrderDetailsComplexResponseDataDriverSolveNode,
): OrderCargoOrderDetailsComplexDriverSolveNode => {
  return {
    checkoutEvent: createDriverSolveNodeCheckoutEvent(node.checkoutEvent),
    nodeId: node.nodeId,
  };
};

const createDriverSolve = (
  solve: OrderCargoOrderDetailsComplexResponseDataDriverSolveNode[] | null,
): OrderCargoOrderDetailsComplexDriverSolveNode[] | null => {
  if (!solve) return null;
  return solve.map(createDriverSolveNode);
};

const createPassengerInternal = (
  passenger: OrderCargoOrderDetailsComplexResponseDataPassengerInternal,
): OrderCargoOrderDetailsComplexPassengerInternal => {
  return {
    cargoItemId: passenger.cargoItemId,
    firstName: passenger.firstName,
    itemType: SourceType.INTERNAL,
    lastName: passenger.lastName,
    passengerUuid: passenger.passengerId,
    phoneNumber: passenger.phoneNo,
    phonePrefix: passenger.phonePrefix,
  };
};

const createPassengerExternal = (
  passenger: OrderCargoOrderDetailsComplexResponseDataPassengerExternal,
): OrderCargoOrderDetailsComplexPassengerExternal => {
  return {
    cargoItemId: passenger.cargoItemId,
    firstName: passenger.firstName,
    itemType: SourceType.EXTERNAL,
    lastName: passenger.lastName,
    phoneNumber: passenger.phoneNo,
    phonePrefix: passenger.phonePrefix,
  };
};

const createPassenger = (
  passenger: OrderCargoOrderDetailsComplexResponseDataPassenger,
): OrderCargoOrderDetailsComplexPassenger => {
  if (
    passenger.itemType ===
    OrderCargoOrderDetailsComplexResponseDataPassengerType.INTERNAL
  ) {
    return createPassengerInternal(passenger);
  } else {
    return createPassengerExternal(passenger);
  }
};

const createPassengers = (
  passengers: OrderCargoOrderDetailsComplexResponseDataPassenger[],
): OrderCargoOrderDetailsComplexPassenger[] => {
  return passengers.map(createPassenger);
};

const createSolveNode = (
  node: OrderCargoOrderDetailsComplexResponseDataSolveNode,
): OrderCargoOrderDetailsComplexSolveNode => {
  return {
    displayName: node.displayName,
    distance: node.distance,
    estimatedTime: node.estimatedTime,
    cargoEnter: node.cargoEnter,
    cargoExit: node.cargoExit,
    lat: node.lat,
    lon: node.lon,
    nodeId: node.nodeId,
    requestedHaltingTime: node.requestedHaltingTime,
    requestedTime: node.requestedTime,
  };
};

const createSolveStage = (
  stage: OrderCargoOrderDetailsComplexResponseDataSolveStage,
): OrderCargoOrderDetailsComplexSolveStage => {
  if (Array.isArray(stage)) {
    return stage.map(createSolveNode);
  } else {
    return createSolveNode(stage);
  }
};

const createSolve = (
  solve: OrderCargoOrderDetailsComplexResponseDataSolveStage[],
): OrderCargoOrderDetailsComplexSolveStage[] => {
  return solve.map(createSolveStage);
};

const createTransportingOrder = (
  transportingOrder: OrderCargoOrderDetailsComplexResponseDataTransportingOrder,
): OrderCargoOrderDetailsComplexTransportingOrder => {
  return {
    createPlanEntryEntries: createCandidatureEntries(
      transportingOrder.createPlanEntryEntries,
    ),
    createSolvedOrderEntries: createCandidatureEntries(
      transportingOrder.createSolvedOrderEntries,
    ),
    driver: createDriver(transportingOrder.driver),
    driverSolve: createDriverSolve(transportingOrder.driverSolve),
    passengers: createPassengers(transportingOrder.passengers),
    planEntryHumanId: transportingOrder.planEntryHumanId,
    planEntryUuid: transportingOrder.planEntryId,
    solve: createSolve(transportingOrder.solve),
    uuid: transportingOrder.id,
  };
};

const createTransportingOrders = (
  transportingOrders: OrderCargoOrderDetailsComplexResponseDataTransportingOrder[],
): OrderCargoOrderDetailsComplexTransportingOrder[] => {
  return transportingOrders.map(createTransportingOrder);
};

const createData = (
  responseData: OrderCargoOrderDetailsComplexResponseData,
): OrderCargoOrderDetailsComplex => {
  return {
    cancelByTaxiAllowed: responseData.cancelByTaxiAllowed,
    contactPassengerAfterMinutes: responseData.contactPassengerAfterMinutes,
    dispatcher: createDispatcher(responseData.dispatcher),
    editByTaxiAllowed: responseData.editByTaxiAllowed,
    externalId: responseData.externalId,
    humanId: responseData.humanId,
    isFinished: responseData.isFinished,
    isCancelled: responseData.isCancelled,
    isCancelRequested: responseData.isCancelRequested,
    isPublished: responseData.isPublished,
    nodeExclusions: createNodeExclusions(responseData.nodeExclusions),
    targetTaxiUuid: responseData.targetTaxiId,
    transportingOrders: createTransportingOrders(
      responseData.transportingOrders,
    ),
    uuid: responseData.id,
  };
};

const create = (
  response: OrderCargoOrderDetailsComplexResponse,
): OrderCargoOrderDetailsComplex => {
  return createData(response.data);
};

const cargoOrderDetailsComplexFactory = { create };

export default cargoOrderDetailsComplexFactory;
